import { faArrowLeft, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  contentWrapper,
  infoWrapper,
  inputsWrapper,
  thumbnailWrapper,
  videoPlayer,
  videoWrapper,
} from '../components/editVideo/EditVideo.css';
import { Player } from '../components/editVideo/Player';
import { Textarea } from '@design-system/components/textarea/textarea';
import { Typography } from '@design-system/components/typography/typography';
import { Button } from '@design-system/components/button/button';
import { sprinkles } from '@design-system/sprinkles.css';
import { Controller } from 'react-hook-form';
import { EditVideoVM } from '../view-models/editVideo.viewModel';
import { useToastEffect } from '../../../../helpers/useNotifcations';
import { InfoAlert } from '../components/InfoAlert';
import { SelectThumbnail } from '../components/SelectThumbnail';
import { TitleInputWithHints } from '@client/new/pages/workout/components/titleInputWithHints/TitleInputWithHints';
import { Hint, HintType } from '@client/new/pages/workout/components/Hint';
import { faPencil } from '@fortawesome/pro-solid-svg-icons';
import { bgColorsVars } from '@design-system/vars.css';
import { LinearProgress } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { clsx } from 'clsx';
import { GenerateDescription } from '@client/new/pages/creator/library/components/GenerateDescription';

export const EditVideoView = (p: EditVideoVM) => {
  useToastEffect(p.notifications);

  return (
    <div className={contentWrapper}>
      {p.editingMode === 'Thumbnail' ? (
        <EditVideoThumbnail {...p} />
      ) : (
        <EditVideoForm {...p} />
      )}
    </div>
  );
};

const ReplaceStatus = (p: EditVideoVM) => (
  <div className="flex flex-col gap-2 w-[260px]">
    <Typography variant="body" size="medium">
      {p.isProcessing
        ? 'Processing... Please wait, it might take a while'
        : 'Uploading in progress... Please do not close the tab!'}
    </Typography>
    <Typography
      variant="body"
      size="medium"
      className={clsx('font-normal', sprinkles({ color: 'N-80' }))}
    >
      All meta data will be kept as in original file
    </Typography>
    {!p.isUploading ? null : (
      <div className="flex gap-4 items-center w-full">
        <div className="flex items-center gap-2 w-full">
          <div className="w-full mr-1 inline">
            <LinearProgress
              variant="determinate"
              value={p.uploadState?.progress ?? 0}
              data-testid="ListItemUploadStatusProgress"
              sx={{
                '&.MuiLinearProgress-root': {
                  width: '100%',
                  background: bgColorsVars['N-20'],
                },
                '&.MuiLinearProgress-root > .MuiLinearProgress-bar1Determinate':
                  {
                    background: bgColorsVars['P-80'],
                  },
              }}
            />
          </div>
          <Typography
            variant="body"
            className={sprinkles({ color: 'P-80' })}
          >{`${Math.round(p.uploadState?.progress ?? 0)}%`}</Typography>
        </div>
        <FontAwesomeIcon
          icon={faXmark}
          className="text-[20px] cursor-pointer"
          onClick={() => p.dispatch('cancelSingleUpload')}
        />
      </div>
    )}
  </div>
);

const EditVideoForm = (p: EditVideoVM) => (
  <div className="flex flex-col w-full gap-4">
    {!p.uploadError || !p.replacingFile ? null : (
      <div className="flex gap-1 items-center bg-[#F5F5F5] rounded-[10px] h-[68px]">
        <video
          className={clsx(
            'w-[52px] h-[52px] rounded-[4px] p-2',
            sprinkles({ clipPath: 'm' }),
          )}
          src={p.replacingFile ? URL.createObjectURL(p.replacingFile) : ''}
          controls={false}
        />
        <div>
          <Typography variant="body" size="large">
            {p.replacingFile.name}
          </Typography>
          <Typography
            variant="body"
            size="medium"
            className={sprinkles({ color: 'ERROR-100' })}
          >
            File failed to upload
          </Typography>
        </div>
        <FontAwesomeIcon
          icon={faXmark}
          className="text-[16px] p-4 ml-auto cursor-pointer"
          onClick={() => p.dispatch('clearReplaceFile')}
        />
      </div>
    )}
    <div className={infoWrapper}>
      <div className={videoWrapper}>
        <Typography>File</Typography>
        <div className={videoPlayer}>
          <Player
            key={p.video.id}
            src={p.video.url}
            poster={p.video.thumbnailUrl}
            duration={p.video.duration}
            height={200}
            width={260}
          />
        </div>
        {p.isReplacing && <ReplaceStatus {...p} />}
        <div className="flex justify-start md:justify-center items-center gap-2">
          <Button
            color="primaryTonal"
            onClick={() => p.dispatch('showReplaceConfirmation')}
            disabled={p.submitting || p.isReplacing}
            className="w-[125px]"
          >
            Replace
          </Button>
          <Button
            variant="text"
            color="neutral"
            onClick={() => p.dispatch('setEditingMode', 'Thumbnail')}
            disabled={p.submitting || p.isReplacing}
            leftIcon={faPencil}
            className="w-[125px]"
          >
            Thumbnail
          </Button>
        </div>
      </div>
      <div className={inputsWrapper}>
        <Controller
          name={'name'}
          control={p.form.control}
          render={({ field: { onChange, value } }) => (
            <TitleInputWithHints
              required={true}
              value={value}
              onChange={onChange}
              disabled={p.submitting}
              error={p.form.errors?.name?.message}
              exerciseSuggestions={p.exerciseSuggestions}
              onUpdate={(description: string, uuid: string) =>
                p.dispatch('exerciseTitleUpdate', {
                  description,
                  uuid,
                })
              }
            />
          )}
        />
        <Hint
          hint={p.inputHints?.[p.uuid]}
          onUpdate={(hint: HintType) =>
            p.dispatch('applyHint', { hint, uuid: p.uuid })
          }
        />
        <Controller
          name={'description'}
          control={p.form.control}
          render={({ field: { onChange, value } }) => (
            <Textarea
              label="Description"
              rows={4}
              disabled={p.submitting}
              value={value}
              onChange={onChange}
              error={p.form.errors?.description?.message}
            />
          )}
        />
        <InfoAlert text="Modifying this won't update the title or description in any exercises where this file has been used in a workout or collection." />
      </div>
    </div>
    {p.exerciseSuggestions.length > 0 && (
      <div
        style={{
          margin: '1px',
          padding: '20px',
          border: 'solid 2px',
          borderColor: bgColorsVars['N-40'],
          borderRadius: 10,
          boxSizing: 'border-box',
          maxWidth: 750,
        }}
      >
        <b className="inline-block mb-3">Suggested Descriptions</b>
        <div className="flex flex-col text-sm">
          {p.exerciseSuggestions.slice(0, 4).map(({ description, name }, i) => (
            <Accordion key={name + i}>
              <AccordionSummary expandIcon={<ArrowDropDownIcon />}>
                <div className="flex flex-row justify-between w-full">
                  <div className="font-bold">{name}</div>{' '}
                  <Button
                    color="primaryTonal"
                    className="w-[80px] mr-2 text-xs"
                    onClick={(event) => {
                      event.stopPropagation();
                      p.form.setValue('description', description);
                    }}
                  >
                    Apply
                  </Button>
                </div>
              </AccordionSummary>
              <AccordionDetails className="whitespace-pre-wrap">
                {description}
              </AccordionDetails>
            </Accordion>
          ))}
        </div>
      </div>
    )}

    {p.canSeeExperimentalFeatures && (
      <div>
        <GenerateDescription
          name={p.generateDescriptions.name}
          bulletForm={p.generateDescriptions.bulletForm}
          concise={p.generateDescriptions.concise}
          generatedDescription={p.generateDescriptions.generatedDescription}
          onGenerate={p.generateDescriptions.handleCreate}
          onAdd={() =>
            p.dispatch('applyGeneratedDescription', {
              name: p.generateDescriptions.name,
              description: p.generateDescriptions.generatedDescription,
            })
          }
          onToggleConcise={() =>
            p.generateDescriptions.setConcise(!p.generateDescriptions.concise)
          }
          onNameChange={(e) => p.generateDescriptions.setName(e.target.value)}
          onToggleBulletForm={() =>
            p.generateDescriptions.setBulletForm(
              !p.generateDescriptions.bulletForm,
            )
          }
        />
      </div>
    )}
  </div>
);

const EditVideoThumbnail = (p: EditVideoVM) => (
  <div className={thumbnailWrapper}>
    <Button
      variant="text"
      onClick={() => p.dispatch('setEditingMode', 'Details')}
      disabled={p.submitting}
      className={sprinkles({
        width: 'max-content',
      })}
    >
      <div
        className={sprinkles({
          display: 'flex',
          alignItems: 'center',
          gap: 'm',
        })}
      >
        <FontAwesomeIcon icon={faArrowLeft} /> Back
      </div>
    </Button>
    <div className={sprinkles({ p: 'l' })}>
      <SelectThumbnail
        video={p.video}
        onChange={(value) => p.dispatch('selectThumbnail', value)}
        onRestore={() => p.dispatch('restoreThumbnail')}
        submitting={p.submitting}
      />
    </div>
  </div>
);
